import { Box, Toolbar, useMediaQuery, useTheme } from '@mui/material';
import { type ReactNode, useState } from 'react';
import { Outlet } from 'react-router-dom';

import { PageTransition } from '@/components/common';
import type { NavGroup, NavItem } from '@/components/layout/NavItem';
import { SideNav } from '@/components/layout/SideNav';
import { TopNav } from '@/components/layout/TopNav';
import { useNavCollapsed } from '@/hooks/useNavCollapsed';

export interface DashboardLayoutProps {
  navItems: readonly NavItem[];
  /** Collapsible nav sections below the flat items (Platform Admin nav). */
  navGroups?: readonly NavGroup[];
  /** Rendered above the routed content — used by `AdminLayout` for its audit-context banner. */
  banner?: ReactNode;
}

/**
 * The generic authenticated shell: top bar + side nav + content outlet. Persona layouts (Sprint
 * 2 — `CandidateLayout`, `RecruiterLayout`, etc., per ADR-0004) render this with their own
 * `navItems`; this component owns only the responsive drawer mechanics, never persona copy.
 */
export function DashboardLayout({ navItems, navGroups, banner }: DashboardLayoutProps) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { collapsed, toggle } = useNavCollapsed();

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <TopNav
        showMenuButton
        onMenuClick={() => setMobileNavOpen(true)}
        onToggleNav={toggle}
        navCollapsed={collapsed}
      />
      <SideNav
        items={navItems}
        groups={navGroups}
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        variant={isDesktop ? 'permanent' : 'temporary'}
        collapsed={collapsed}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        {banner}
        <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
          <PageTransition>
            <Outlet />
          </PageTransition>
        </Box>
      </Box>
    </Box>
  );
}
